/// <amd-module name="Repos/Views/PullRequestUpdates/PullRequestUpdatesService" />
import {
    AutoCompleteUpdatedEvent,
    BranchUpdatedEvent,
    CompletionErrorsEvent,
    DiscussionsUpdatedEvent,
    GitPullRequest,
    IsDraftUpdatedEvent,
    LabelsUpdatedEvent,
    MergeCompletedEvent,
    PolicyEvaluationUpdatedEvent,
    RealTimePullRequestEvent,
    RetargetEvent,
    ReviewersUpdatedEvent,
    ReviewersVotesResetEvent,
    ReviewerVoteUpdatedEvent,
    StatusAddedEvent,
    StatusesDeletedEvent,
    StatusUpdatedEvent,
    TitleDescriptionUpdatedEvent
} from "Repos/Common/Contracts/Git";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { IPullRequestUpdatesService } from "Repos/Views/PullRequest/Contracts/PullRequestUpdates";
import { IInternalPullRequestDataService, PullRequestDataTypes } from "Repos/Views/PullRequest/PullRequestDataService";
import { getPullRequestViewService, IPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import * as Resources from "Repos/Views/PullRequestUpdates/Resources";
import { getTFSPageData } from "Tfs/Platform/Page";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IHubHostInformation, SignalRHub } from "VSS/Features/SignalR/Hub";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssPageService } from "VSS/Platform/Page";
import { publishEvent } from "VSS/Platform/Telemetry";
import { MessageCardSeverity } from "VSSUI/MessageCard";

const throttledEventTimeoutMs = 5000; // 5 seconds
const signalRDisconnectedMessageType = "pr-updates-disconnected";

class PullRequestUpdatesService extends VssService implements IPullRequestUpdatesService {
    private callbacks: IPullRequestSignalRHubCallbacks;
    private dataService: IInternalPullRequestDataService;
    private listening: boolean;
    private pullRequestId: number;
    private pullRequestViewService: IPullRequestViewService;
    private repositoryId: string;
    private signalRHub?: PullRequestSignalRHub;
    private timerManagement = new TimerManagement();

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.dataService = this.pageContext.getService<IInternalPullRequestDataService>("IInternalPullRequestDataService");

        this.pullRequestViewService = getPullRequestViewService(this.pageContext);
        this.pullRequestId = this.pullRequestViewService.getId();
        this.repositoryId = this.pullRequestViewService.getTargetRepository().id;

        const createThrottledCallback = (func: () => void): (() => void) => {
            return this.timerManagement.throttle(func, throttledEventTimeoutMs, { leading: true, trailing: true });
        };

        this.refreshAutoCompleteData = createThrottledCallback(this.refreshAutoCompleteData);
        this.refreshBasicDetails = createThrottledCallback(this.refreshBasicDetails);
        this.refreshDiscussions = createThrottledCallback(this.refreshDiscussions);
        this.refreshPolicies = createThrottledCallback(this.refreshPolicies);
        this.refreshStatuses = createThrottledCallback(this.refreshStatuses);
        this.refreshLabels = createThrottledCallback(this.refreshLabels);
        this.refreshSourceBranch = createThrottledCallback(this.refreshSourceBranch);
        this.refreshTargetBranch = createThrottledCallback(this.refreshTargetBranch);

        this.callbacks = {
            onAutoCompleteUpdated: (autoCompleteUpdatedEvent: AutoCompleteUpdatedEvent): void => {
                this.refreshAutoCompleteData();
            },
            onBranchUpdated: (branchUpdatedEvent: BranchUpdatedEvent): void => {
                if (branchUpdatedEvent.isSourceUpdate) {
                    this.refreshSourceBranch();
                } else {
                    this.refreshTargetBranch();
                }
            },
            onCompletionErrors: (completionErrorsEvent: CompletionErrorsEvent): void => {
                this.showErrorMessage(completionErrorsEvent.errorMessage);
            },
            onConnected: (pullRequest: GitPullRequest, initialConnection: boolean): void => {
                this.removeDisconnectedBanner();

                // Pick up any updates that were missed before the connection was established.
                this.dataService.refreshPullRequestData(PullRequestDataTypes.default);
            },
            onDisconnected: (): void => {
                this.showDisconnectedBanner();
            },
            onDiscussionsUpdated: (discussionsUpdatedEvent: DiscussionsUpdatedEvent): void => {
                this.refreshDiscussions();
            },
            onIsDraftUpdated: (isDraftUpdatedEvent: IsDraftUpdatedEvent): void => {
                this.refreshSourceBranch();
            },
            onLabelsUpdated: (labelsUpdatedEvent: LabelsUpdatedEvent): void => {
                this.refreshLabels();
            },
            onMergeCompleted: (mergeCompletedEvent: MergeCompletedEvent): void => {
                this.refreshSourceBranch();
            },
            onPolicyEvaluationUpdated: (policyEvaluationUpdatedEvent: PolicyEvaluationUpdatedEvent): void => {
                this.refreshPolicies();
            },
            onReviewersUpdated: (reviewersUpdatedEvent: ReviewersUpdatedEvent): void => {
                this.refreshBasicDetails();
                this.refreshPolicies();
            },
            onReviewersVotesReset: (reviewersVotesResetEvent: ReviewersVotesResetEvent): void => {
                this.refreshBasicDetails();
                this.refreshPolicies();
            },
            onReviewerVoteUpdated: (reviewerVoteUpdatedEvent: ReviewerVoteUpdatedEvent): void => {
                this.refreshBasicDetails();
                this.refreshPolicies();
            },
            onStatusAdded: (statusAddedEvent: StatusAddedEvent): void => {
                this.refreshStatuses();
            },
            onStatusesDeleted: (statusesDeletedEvent: StatusesDeletedEvent): void => {
                this.refreshStatuses();
            },
            onStatusUpdated: (statusUpdatedEvent: StatusUpdatedEvent): void => {
                this.refreshStatuses();
            },
            onTitleDescriptionUpdated: (titleDescriptionUpdatedEvent: TitleDescriptionUpdatedEvent): void => {
                this.refreshBasicDetails();
            },
            onRealTimePullRequestUpdated: (realTimePullRequestEvent: RealTimePullRequestEvent): void => {},
            onTargetChanged: (retargetEvent: RetargetEvent): void => {
                this.refreshSourceBranch();
            }
        };
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        if (this.signalRHub) {
            this.signalRHub.dispose();
            this.signalRHub = undefined;
        }
    }

    public enableLiveUpdates(): void {
        if (!this.listening) {
            if (!this.signalRHub) {
                const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;
                this.signalRHub = new PullRequestSignalRHub(
                    this.pageContext,
                    {
                        hostId: pageData.hostId,
                        isHosted: pageData.isHosted,
                        hostPath: pageData.hostPath,
                        projectId: getTFSPageData(this.pageContext)!.project!.id
                    },
                    this.pullRequestId,
                    this.repositoryId,
                    this.callbacks
                );
            }

            this.signalRHub.watch();
            this.listening = true;
        }
    }

    public disableLiveUpdates(): void {
        if (this.listening) {
            if (this.signalRHub) {
                this.signalRHub.endWatch();
            }
            this.listening = false;
        }
    }

    private removeDisconnectedBanner(): void {
        const messageService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
        const message = messageService.getMessage(false).value;
        if (message && message.type === signalRDisconnectedMessageType) {
            messageService.dismissMessage();
        }
    }

    private showDisconnectedBanner(): void {
        const messageService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
        messageService.showMessage({
            buttons: [
                {
                    onClick: e => {
                        onClickFPS(this.pageContext, window.location.href, false, e);
                    },
                    text: Resources.RefreshPage
                }
            ],
            renderMessage: () => Resources.DisconnectedWarning,
            severity: MessageCardSeverity.Warning,
            type: signalRDisconnectedMessageType
        });
    }

    private refreshAutoCompleteData = (): void => {
        this.dataService.refreshPullRequestData(
            PullRequestDataTypes.basicDetails | PullRequestDataTypes.autoComplete | PullRequestDataTypes.discussionThreads
        );
    };
    private refreshBasicDetails = (): void => {
        this.dataService.refreshPullRequestData(PullRequestDataTypes.basicDetails | PullRequestDataTypes.discussionThreads);
    };
    private refreshDiscussions = (): void => {
        this.dataService.refreshPullRequestData(PullRequestDataTypes.discussionThreads);
    };
    private refreshPolicies = (): void => {
        this.dataService.refreshPullRequestData(PullRequestDataTypes.policies | PullRequestDataTypes.autoComplete);
    };
    private refreshStatuses = (): void => {
        this.dataService.refreshPullRequestData(PullRequestDataTypes.statuses);
    };
    private refreshLabels = (): void => {
        this.dataService.refreshPullRequestData(PullRequestDataTypes.labels);
    };
    private refreshSourceBranch = (): void => {
        this.dataService.refreshPullRequestData(PullRequestDataTypes.default);
    };
    private refreshTargetBranch = (): void => {
        this.dataService.refreshPullRequestData(PullRequestDataTypes.basicDetails | PullRequestDataTypes.policies | PullRequestDataTypes.statuses);
    };

    private showErrorMessage = (errorMessage: string) => {
        const messageService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
        messageService.showMessage({
            renderMessage: () => errorMessage,
            severity: MessageCardSeverity.Error
        });
    };
}

Services.add("IPullRequestUpdatesService", { serviceFactory: PullRequestUpdatesService });

interface IPullRequestSignalRHubServer {
    subscribe: (pullRequestId: number, repositoryId: string) => Promise<GitPullRequest>;
    unsubscribe: (pullRequestId: number) => Promise<void>;
}

interface IPullRequestSignalRHubClientEvents {
    onAutoCompleteUpdated: (autoCompleteUpdatedEvent: AutoCompleteUpdatedEvent) => void;
    onBranchUpdated: (branchUpdatedEvent: BranchUpdatedEvent) => void;
    onCompletionErrors: (completionErrorsEvent: CompletionErrorsEvent) => void;
    onDisconnected: () => void;
    onDiscussionsUpdated: (discussionsUpdatedEvent: DiscussionsUpdatedEvent) => void;
    onIsDraftUpdated: (isDraftUpdatedEvent: IsDraftUpdatedEvent) => void;
    onLabelsUpdated: (labelsUpdatedEvent: LabelsUpdatedEvent) => void;
    onMergeCompleted: (mergeCompletedEvent: MergeCompletedEvent) => void;
    onPolicyEvaluationUpdated: (policyEvaluationUpdatedEvent: PolicyEvaluationUpdatedEvent) => void;
    onReviewersUpdated: (reviewersUpdatedEvent: ReviewersUpdatedEvent) => void;
    onReviewersVotesReset: (reviewersVotesResetEvent: ReviewersVotesResetEvent) => void;
    onReviewerVoteUpdated: (reviewerVoteUpdatedEvent: ReviewerVoteUpdatedEvent) => void;
    onStatusAdded: (statusAddedEvent: StatusAddedEvent) => void;
    onStatusesDeleted: (statusesDeletedEvent: StatusesDeletedEvent) => void;
    onStatusUpdated: (statusUpdatedEvent: StatusUpdatedEvent) => void;
    onTitleDescriptionUpdated: (titleDescriptionUpdatedEvent: TitleDescriptionUpdatedEvent) => void;
    onRealTimePullRequestUpdated: (realTimePullRequestEvent: RealTimePullRequestEvent) => void;
    onTargetChanged: (retargetEvent: RetargetEvent) => void;
}

interface IPullRequestSignalRHubCallbacks extends IPullRequestSignalRHubClientEvents {
    onConnected(pullRequest: GitPullRequest, isInitialConnection: boolean): void;
}

const pullRequestHubName = "pullrequestdetailhub";

// Disconnect SignalR connections after 1 minute of user inactivity
const userInactivityTimeoutMs = 60000;

class PullRequestSignalRHub extends SignalRHub<IPullRequestSignalRHubClientEvents, IPullRequestSignalRHubServer> {
    private callbacks: IPullRequestSignalRHubCallbacks;
    private connectionStarted = false;
    private initialConnection = true;
    private pageContext: IVssPageContext;
    private pullRequestId: number;
    private repositoryId: string;
    private disposing = false;

    constructor(
        pageContext: IVssPageContext,
        hostInformation: IHubHostInformation,
        pullRequestId: number,
        repositoryId: string,
        callbacks: IPullRequestSignalRHubCallbacks
    ) {
        super({
            name: pullRequestHubName,
            hostInformation,
            listener: {
                isConnecting: () => {},
                isConnected: () => {
                    this.subscribe();
                    this.initialConnection = false;
                },
                isDisconnected: () => {
                    if (!this.disposing && this.connectionStarted) {
                        this.callbacks.onDisconnected();
                        this.sendTelemetry("disconnected");
                    }
                },
                isReconnected: () => {
                    this.subscribe();
                    this.initialConnection = false;
                    this.sendTelemetry("reconnected");
                },
                isReconnecting: () => {},
                isSlow: () => {
                    this.initialConnection = false;
                    this.sendTelemetry("slow-connection");
                },
                isUserActive: () => {
                    if (!this.connectionStarted) {
                        this.watch();
                        this.sendTelemetry("user-reactivate");
                    }
                },
                isUserInactive: () => {
                    this.endWatch();
                }
            },
            pageContext,
            userInactivityTimeoutMs
        });

        this.pageContext = pageContext;
        this.callbacks = callbacks;
        this.pullRequestId = pullRequestId;
        this.repositoryId = repositoryId;

        this.initializeProxy(
            baseProxy => {
                baseProxy.server = {
                    subscribe: (pullRequestId: number, repositoryId: string) => {
                        return baseProxy.invoke.apply(baseProxy, ["Subscribe", pullRequestId, repositoryId]);
                    },
                    unsubscribe: (pullRequestId: number) => {
                        return baseProxy.invoke.apply(baseProxy, ["Unsubscribe", pullRequestId]);
                    }
                };
                return baseProxy;
            },
            proxy => {
                proxy.client.onAutoCompleteUpdated = e => callbacks.onAutoCompleteUpdated(e);
                proxy.client.onBranchUpdated = e => callbacks.onBranchUpdated(e);
                proxy.client.onCompletionErrors = e => callbacks.onCompletionErrors(e);
                proxy.client.onDiscussionsUpdated = e => callbacks.onDiscussionsUpdated(e);
                proxy.client.onIsDraftUpdated = e => callbacks.onIsDraftUpdated(e);
                proxy.client.onLabelsUpdated = e => callbacks.onLabelsUpdated(e);
                proxy.client.onMergeCompleted = e => callbacks.onMergeCompleted(e);
                proxy.client.onPolicyEvaluationUpdated = e => callbacks.onPolicyEvaluationUpdated(e);
                proxy.client.onReviewersUpdated = e => callbacks.onReviewersUpdated(e);
                proxy.client.onReviewersVotesReset = e => callbacks.onReviewersVotesReset(e);
                proxy.client.onReviewerVoteUpdated = e => callbacks.onReviewerVoteUpdated(e);
                proxy.client.onStatusAdded = e => callbacks.onStatusAdded(e);
                proxy.client.onStatusesDeleted = e => callbacks.onStatusesDeleted(e);
                proxy.client.onStatusUpdated = e => callbacks.onStatusUpdated(e);
                proxy.client.onTitleDescriptionUpdated = e => callbacks.onTitleDescriptionUpdated(e);
                proxy.client.onRealTimePullRequestUpdated = e => callbacks.onRealTimePullRequestUpdated(e);
                proxy.client.onTargetChanged = e => callbacks.onTargetChanged(e);
            }
        );
    }

    public dispose() {
        this.disposing = true;
        super.dispose();
    }

    public async watch(): Promise<void> {
        if (!this.connectionStarted) {
            this.connectionStarted = true;
            try {
                await this.start();
            } catch (ex) {
                this.connectionStarted = false;
                this.initialConnection = false;
                if (!this.disposing) {
                    this.callbacks.onDisconnected();
                    this.sendTelemetry("initialConnectFailed", ex && ex.message);
                }
            }
        }
    }

    public endWatch(): void {
        this.connectionStarted = false;
        this.stop(true);
    }

    private async subscribe(): Promise<void> {
        if (this.hub) {
            const initialConnection = this.initialConnection;
            const pullRequest = await this.hub.server.subscribe(this.pullRequestId, this.repositoryId);
            this.callbacks.onConnected(pullRequest, initialConnection);
        }
    }

    private sendTelemetry(activity: string, details?: string): void {
        publishEvent(this.pageContext, "Repos", "SignalR", {
            activity,
            details
        });
    }
}
